import React, { useEffect, useState } from "react";
import {
    Box, Typography, Autocomplete, TextField, Button, Alert, CircularProgress, Divider, Chip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../../constants";
import { loyaltyFetch } from "../../../hooks/loyaltyFetch";
import { autocompletePopperSlotProps } from "../../../utils/autocompletePopperSlotProps";

/**
 * Gère les liens catalogue coupon ↔ rewards via PUT /admin/coupon-types/{id}/rewards
 * (pratique recommandée — pas de PATCH reward avec coupon_type_ids).
 */
const CouponTypeRewardsLinker = ({ couponType, brandName, onSaved }) => {
    const [catalogRewards, setCatalogRewards] = useState([]);
    const [linkedIds, setLinkedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!couponType?.id || !brandName) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        const headers = { "X-Brand": brandName };
        Promise.all([
            loyaltyFetch(`${LOYALTY_API_BASE}/rewards?brand=${encodeURIComponent(brandName)}&active=true`, { headers })
                .then((r) => (r.ok ? r.json() : []))
                .then((data) => (Array.isArray(data) ? data : [])),
            loyaltyFetch(`${LOYALTY_API_BASE}/admin/coupon-types/${couponType.id}/rewards`, { headers })
                .then((r) => (r.ok ? r.json() : []))
                .then((data) => (Array.isArray(data) ? data : [])),
        ])
            .then(([allRewards, linked]) => {
                if (cancelled) return;
                setCatalogRewards(allRewards);
                setLinkedIds(linked.map((r) => String(r.id)));
            })
            .catch((err) => { if (!cancelled) setError(err.message); })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [couponType?.id, brandName]);

    const selectedRewards = catalogRewards.filter((r) => linkedIds.includes(String(r.id)));

    const handleSaveLinks = async () => {
        if (!couponType?.id || !brandName) return;
        setSaving(true);
        setError(null);
        try {
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/admin/coupon-types/${couponType.id}/rewards`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json", "X-Brand": brandName },
                    body: JSON.stringify({ reward_ids: linkedIds }),
                },
            );
            const data = await res.json().catch(() => null);
            if (!res.ok) throw new Error(data?.detail || `Erreur HTTP ${res.status}`);
            onSaved?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    if (!couponType) return null;

    return (
        <Box mt={2}>
            <Divider sx={{ mb: 2 }} />
            <Typography fontWeight={800} fontFamily="Montserrat, sans-serif" fontSize={13} color="#1a1a2e" mb={0.5}>
                Récompenses du catalogue liées
            </Typography>
            <Typography fontSize={11} color="text.secondary" mb={1.5}>
                Ordre et composition du lot émis par ce type de coupon (PUT …/coupon-types/…/rewards).
            </Typography>

            {loading ? (
                <Box display="flex" alignItems="center" gap={1} py={1}>
                    <CircularProgress size={18} />
                    <Typography fontSize={12} color="text.secondary">Chargement…</Typography>
                </Box>
            ) : (
                <>
                    <Autocomplete
                        multiple
                        slotProps={autocompletePopperSlotProps}
                        options={catalogRewards}
                        value={selectedRewards}
                        isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                        getOptionLabel={(o) => o.name || String(o.id)}
                        onChange={(_, vals) => setLinkedIds(vals.map((v) => String(v.id)))}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option.id}
                                    label={option.name}
                                    size="small"
                                    sx={{ fontWeight: 600, fontSize: 11 }}
                                />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="Sélectionner les rewards…"
                                helperText={`${linkedIds.length} reward(s) liée(s) — l’ordre de sélection est conservé`}
                                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                            />
                        )}
                    />
                    <Box display="flex" justifyContent="flex-end" mt={1.5}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveLinks}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 2,
                                background: SECTION_COLORS.rewards.gradient,
                            }}
                        >
                            Enregistrer les liens
                        </Button>
                    </Box>
                </>
            )}
            {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
        </Box>
    );
};

export default CouponTypeRewardsLinker;
